import { Component, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { ConfirmDialogV2Component } from 'src/app/global/components/dialogs/confirm-dialog-v2/confirm-dialog-v2.component';
import { ErrorSeverity } from 'src/app/global/components/dialogs/informational-dialog/informational-dialog.component';
import { State } from 'src/app/store';
import { InformationalDialogV2Component } from 'src/app/global/components/dialogs/informational-dialog-v2/informational-dialog-v2.component';
import { RequestDepartment } from '../../../../models/request-department.model';
import { RequestDepartmentService } from '../../../../services/request-department.service';
import { MessageService } from '../../../../../global/services/message/message.abstract.service';

@Component({
    selector: 'app-department-list',
    templateUrl: './department-list.component.html',
    styleUrls: ['./department-list.component.scss'],
})
export class DepartmentListComponent implements OnInit, OnDestroy {
    @Output() edit = new EventEmitter<RequestDepartment>();

    departments: RequestDepartment[] = [];

    loading = false;

    private destroy$ = new Subject<void>();

    constructor(
        private departmentService: RequestDepartmentService,
        private dialog: MatDialog,
        private translate: TranslateService,
        private messageService: MessageService,
        private store: Store<State>,
    ) {}

    ngOnInit(): void {
        this.loadDepartments();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    loadDepartments(): void {
        this.loading = true;
        this.departmentService
            .getRequestDepartments()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (departments) => {
                    this.departments = departments || [];
                    this.loading = false;
                },
                error: (error) => {
                    console.error('Error loading departments:', error);
                    this.loading = false;
                    this.showErrorDialog('request_v2.department_list.load_error');
                    this.showErrorMessage('request_v2.department_list.load_error');
                },
            });
    }

    onEdit(department: RequestDepartment): void {
        this.edit.emit(department);
    }

    onDelete(department: RequestDepartment): void {
        const confirmDialogRef = this.dialog.open(ConfirmDialogV2Component, {
            autoFocus: false,
            panelClass: 'bciti-confirmation-dialog-v2',
            data: {
                title: this.translate.instant('request_v2.department_list.delete.title'),
                body: this.translate.instant('request_v2.department_list.delete.body', {
                    name: this.getDepartmentName(department),
                }),
                buttons: {
                    confirm: {
                        text: this.translate.instant('common.delete'),
                        class: 'bciti-button bciti-button--red',
                    },
                    cancel: {
                        text: this.translate.instant('common.cancel'),
                        class: 'bciti-button bciti-button--blue-inversed',
                    },
                },
            },
        });

        confirmDialogRef.componentInstance.onSubmit.subscribe((confirm) => {
            if (confirm) {
                confirmDialogRef.componentInstance.loading = true;
                this.departmentService
                    .deleteRequestDepartment(department._id)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                        next: () => {
                            confirmDialogRef.close();
                            this.loadDepartments();
                            this.showSuccessMessage('request_v2.department_list.delete_success');
                        },
                        error: (error) => {
                            confirmDialogRef.componentInstance.loading = false;
                            this.showErrorDialog(error?.error?.messages?.[0]);
                        },
                    });
            } else {
                confirmDialogRef.close();
            }
        });
    }

    private showSuccessMessage(key: string): void {
        this.messageService.success(this.translate.instant(key));
    }

    private showErrorMessage(key: string): void {
        this.messageService.error(this.translate.instant(key));
    }

    private showErrorDialog(errorMessage: string): void {
        this.dialog.open(InformationalDialogV2Component, {
            autoFocus: false,
            panelClass: 'informational-dialog',
            data: {
                body: errorMessage || this.translate.instant('common.errors.retry'),
                errorSeverity: ErrorSeverity.Error,
                buttonText: this.translate.instant('common.retry'),
            },
        });
    }

    getDepartmentName(department: RequestDepartment): string {
        return department.name;
    }
}
